/**
 * Player versions that can parse and ACK `report_kk_app_meta`.
 * Player #53 (2026.10.25.0 / versionCode 202610250) is the first
 * release with ReportAppMeta. Older Players (including 2026.10.24.1)
 * ignore the command (`parseFromPubNubMessage` → null, no ACK).
 */

export const REMOTE_SETTINGS_MIN_PLAYER_VERSION_NAME = "2026.10.25.0";
export const REMOTE_SETTINGS_MIN_PLAYER_VERSION_CODE = 202610250;

const VERSION_NAME_RE = /(\d{4})\.(\d{1,2})\.(\d{1,2})\.(\d)/;

export function parsePlayerVersionCode(
  versionName: string | null | undefined
): number | null {
  if (typeof versionName !== "string") return null;
  const match = versionName.trim().match(VERSION_NAME_RE);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const patch = Number(match[4]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(patch)
  ) {
    return null;
  }
  return year * 100000 + month * 1000 + day * 10 + patch;
}

export function formatPlayerVersionName(
  versionName: string | null | undefined
): string | null {
  if (typeof versionName !== "string") return null;
  const match = versionName.trim().match(VERSION_NAME_RE);
  return match ? match[0] : null;
}

/**
 * true  — Player can handle report_kk_app_meta
 * false — known older Player; do not auto-send
 * null  — version missing/unparseable; do not auto-send (fail closed)
 */
export function playerSupportsRemoteSettingsReport(
  versionName: string | null | undefined
): boolean | null {
  const code = parsePlayerVersionCode(versionName);
  if (code == null) return null;
  return code >= REMOTE_SETTINGS_MIN_PLAYER_VERSION_CODE;
}

export const REMOTE_SETTINGS_OPERATOR_COPY = {
  reading: "Reading current settings…",
  versionUnreported:
    "Installed Player version is not reported. Remote Settings was not sent.",
  couldNotRead:
    "Could not read current settings. Confirm is disabled so unknown values are not sent.",
} as const;

export function remoteSettingsUnsupportedCopy(
  versionName: string | null | undefined
): string {
  const shown =
    formatPlayerVersionName(versionName) || "an older Player";
  return `Remote Settings requires Player ${REMOTE_SETTINGS_MIN_PLAYER_VERSION_NAME} or newer. This device is running ${shown}. Settings were not sent.`;
}
